import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Flame, Zap } from "lucide-react";
import { SUPPLY_STATUS } from "@/lib/scadenze";

const EMPTY = {
  type: "Luce", pod_pdr: "", supply_address: "", cap: "", comune: "", provincia: "",
  power_kw: "", annual_consumption: "", status: "Attiva", notes: "",
};

export default function SupplyDialog({ open, onOpenChange, customerId, supply, onSaved }) {
  const editing = !!supply;
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setF(supply ? {
      type: supply.type, pod_pdr: supply.pod_pdr || "", supply_address: supply.supply_address || "",
      cap: supply.cap || "", comune: supply.comune || "", provincia: supply.provincia || "",
      power_kw: supply.power_kw ?? "", annual_consumption: supply.annual_consumption ?? "",
      status: supply.status || "Attiva", notes: supply.notes || "",
    } : { ...EMPTY });
  }, [open, supply]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const isLuce = f.type === "Luce";
  const podLabel = isLuce ? "POD" : "PDR";

  const save = async () => {
    setError(""); setSaving(true);
    try {
      const payload = {
        ...f,
        power_kw: f.power_kw === "" ? null : Number(f.power_kw),
        annual_consumption: f.annual_consumption === "" ? null : Number(f.annual_consumption),
      };
      const { data } = editing
        ? await api.put(`/supplies/${supply.id}`, payload)
        : await api.post(`/customers/${customerId}/supplies`, payload);
      toast.success(editing ? "Fornitura aggiornata" : "Fornitura aggiunta");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-xl" data-testid="supply-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            {isLuce ? <Zap className="h-5 w-5 text-warning" /> : <Flame className="h-5 w-5 text-brand" />}
            {editing ? "Modifica fornitura" : "Nuova fornitura"}
          </DialogTitle>
          <DialogDescription>Punto fisico di fornitura. Il POD identifica la Luce, il PDR il Gas.</DialogDescription>
        </DialogHeader>
        {error && <div className="rounded-lg border border-danger/25 bg-danger/10 px-3 py-2 text-sm text-danger" data-testid="supply-error">{error}</div>}

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Tipo fornitura</Label>
              <Select value={f.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="supply-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="Luce">Luce (POD)</SelectItem>
                  <SelectItem value="Gas">Gas (PDR)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{podLabel}</Label>
              <Input value={f.pod_pdr} onChange={(e) => set("pod_pdr", e.target.value.toUpperCase())} placeholder={podLabel} data-testid="supply-pod" />
            </div>
          </div>

          <div className="grid grid-cols-6 gap-3">
            <div className="col-span-4 space-y-1.5"><Label>Indirizzo fornitura</Label><Input value={f.supply_address} onChange={(e) => set("supply_address", e.target.value)} data-testid="supply-address" /></div>
            <div className="space-y-1.5"><Label>CAP</Label><Input value={f.cap} onChange={(e) => set("cap", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Prov.</Label><Input value={f.provincia} maxLength={4} onChange={(e) => set("provincia", e.target.value)} /></div>
          </div>
          <div className="space-y-1.5"><Label>Comune</Label><Input value={f.comune} onChange={(e) => set("comune", e.target.value)} data-testid="supply-comune" /></div>

          <div className="grid grid-cols-2 gap-3">
            {isLuce && (
              <div className="space-y-1.5"><Label>Potenza impegnata (kW)</Label><Input type="number" step="0.1" value={f.power_kw} onChange={(e) => set("power_kw", e.target.value)} data-testid="supply-power" /></div>
            )}
            <div className="space-y-1.5"><Label>Consumo annuo ({isLuce ? "kWh" : "Smc"})</Label><Input type="number" step="1" value={f.annual_consumption} onChange={(e) => set("annual_consumption", e.target.value)} data-testid="supply-consumption" /></div>
          </div>

          <div className="space-y-1.5">
            <Label>Stato fornitura</Label>
            <Select value={f.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="supply-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{SUPPLY_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5"><Label>Note</Label><Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} /></div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="supply-cancel">Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="supply-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
